import { useState } from 'react';
import { MatchConfirmedContent } from './MatchConfirmedContent';
import { MatchConfirmingContent } from './MatchConfirmingContent';
import { OrgRepConfirmMatchContent } from './OrgRepConfirmMatchContent';
import { MatchDeclinedContent } from './MatchDeclinedContent';
import { MatchDecliningContent } from './MatchDecliningContent';
import { Modal, ModalCallToAction } from '@platform/components';
import { NameCustomProjectContent } from './NameCustomProjectContent';
import * as Sentry from '@sentry/react';
import { OtherUpdateContent } from './OtherUpdateContent';
import { ScheduledContent } from './ScheduledContent';
import { StillDecidingContent } from './StillDecidingContent';
import { MatchedElsewhereContent } from './MatchedElsewhereContent';
import { UnresponsiveContent } from './UnresponsiveContent';
import { UpdateConfirmedContent } from './UpdateConfirmedContent';
import { UpdateSelectionContent } from './UpdateSelectionContent';
import { ProConfirmMatch } from './ProConfirmMatch';
import { useQueryClient } from '@tanstack/react-query';
import { AssignmentDeclinedReason, SimplifiedUserRole } from '@platform/client/schemas';
import { Discovery, MyProjects, ProjectHub } from '@platform/client';
import { navigate } from 'wouter/use-browser-location';
import { ROUTES } from '../../../../../app/routes/consts';
import { useForm } from 'react-hook-form';
import {
  DiscoveryCallModalStage,
  DiscoveryModalProps,
  DiscoveryStatus,
  UpdateDiscoveryCallFormData,
} from './types';

// Shared enums/types live in ./types to avoid a circular import with the content components
export * from './types';

const toISODate = (date: Date): string => date.toISOString().substring(0, 10);

// Statuses whose fields are collected inline under the dropdown on Update_Selection and submitted
// directly with "Save & continue" — no separate stage, for every user type. MatchedElsewhere is
// handled separately (inline for reps, standalone page for pros); see isInlineStatus below.
const INLINE_STATUSES = new Set<DiscoveryStatus>([
  DiscoveryStatus.Scheduled,
  DiscoveryStatus.StillDeciding,
  DiscoveryStatus.DifferentUpdate,
]);

// Status-specific copy for the shared Update_Confirmed success screen; statuses not listed
// fall back to UpdateConfirmedContent's default message.
const UPDATE_CONFIRMED_MESSAGES: Partial<Record<DiscoveryStatus, string>> = {
  [DiscoveryStatus.StillDeciding]:
    'Thanks for the update! No rush, we will check back in with you in a week to see if you’re ready to move forward.',
  [DiscoveryStatus.Unresponsive]:
    "Thanks for the update! We'll follow up with you via email if needed.",
};

export const DiscoveryCallUpdateModal = (props: DiscoveryModalProps) => {
  const { introductionId, otherPartyName, onClose, userType } = props;

  const { register, setValue, control, trigger, formState, watch } =
    useForm<UpdateDiscoveryCallFormData>({
      mode: 'onChange',
      defaultValues: { requestedMatchingSupport: false },
    });
  const selectedStatus = watch('status');
  const isPro = userType === SimplifiedUserRole.PROFESSIONAL;

  // MatchedElsewhere is inline for reps but gets its own page for pros; the rest are inline for everyone.
  const isInlineStatus = (status: DiscoveryStatus | null | undefined): boolean => {
    if (!status) return false;
    if (status === DiscoveryStatus.MatchedElsewhere) return !isPro;
    return INLINE_STATUSES.has(status);
  };

  const selectedReason = watch('reason');
  const declineReasonDetails = watch('declineReasonDetails');
  const otherUpdateDetails = watch('otherUpdateDetails');
  const additionalDetails = watch('additionalDetails');
  const requestedMatchingSupport = watch('requestedMatchingSupport');
  const customProjectName = watch('projectName');

  const [isSubmissionSaving, setIsSubmissionSaving] = useState(false);
  const [modalStage, setModalStage] = useState<DiscoveryCallModalStage>(
    DiscoveryCallModalStage.Update_Selection,
  );
  const [isSubmissionErrored, setIsSubmissionErrored] = useState(false);
  const queryClient = useQueryClient();
  const updateDiscoveryStatus = Discovery.useUpdateDiscoveryCall({
    mutation: {
      onMutate: () => {
        setIsSubmissionSaving(true);
      },
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetProUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getGetOrgUserMyProjectsQueryKey() });
        queryClient.invalidateQueries({ queryKey: MyProjects.getCountUserActionItemsQueryKey() });
        setIsSubmissionSaving(false);
        queryClient.invalidateQueries({
          queryKey: ProjectHub.getGetProjectHubDataQueryKey({
            assignment_discovery_id: props.assignmentDiscoveryId,
          }),
        });
      },
      onError: (error) => {
        Sentry.captureException(error);
        setIsSubmissionSaving(false);
      },
    },
  });

  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const [callDate, setCallDate] = useState<Date>(tomorrow);
  const [startDate, setStartDate] = useState<Date>(new Date());
  const initialEndDate = new Date();
  initialEndDate.setDate(initialEndDate.getDate() + 90);
  const [endDate, setEndDate] = useState<Date>(initialEndDate);

  const closeModal = () => {
    setModalStage(DiscoveryCallModalStage.Update_Selection);
    setIsSubmissionErrored(false);
    setCallDate(tomorrow);
    setStartDate(new Date());
    setEndDate(initialEndDate);
    onClose();
  };

  /** Routes mutate() to the right success stage and surfaces an error otherwise. */
  const mutateOptions = (successStage: DiscoveryCallModalStage) => ({
    onSuccess: () => {
      setIsSubmissionErrored(false);
      setModalStage(successStage);
    },
    onError: () => {
      setIsSubmissionErrored(true);
    },
  });

  const submitAcceptedPro = () => {
    updateDiscoveryStatus.mutate(
      {
        data: {
          introductionId,
          updateType: 'discovery-completed',
          outcome: 'accepted-pro',
          startDate: toISODate(startDate),
          endDate: toISODate(endDate),
          projectName: props.projectName ?? customProjectName,
          additionalDetails: additionalDetails ?? null,
        },
      },
      mutateOptions(DiscoveryCallModalStage.Match_Confirmed),
    );
  };

  /** Submits the fields collected inline under the dropdown for an INLINE_STATUSES selection. */
  const submitInlineUpdate = async () => {
    switch (selectedStatus) {
      case DiscoveryStatus.Scheduled:
        updateDiscoveryStatus.mutate(
          {
            data: {
              introductionId,
              updateType: 'discovery-scheduled',
              callDate: toISODate(callDate),
              additionalDetails: additionalDetails ?? null,
            },
          },
          mutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
        break;
      case DiscoveryStatus.StillDeciding:
        updateDiscoveryStatus.mutate(
          {
            data: {
              introductionId,
              updateType: 'still-deciding',
              additionalDetails: additionalDetails ?? null,
            },
          },
          mutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
        break;
      case DiscoveryStatus.MatchedElsewhere:
        updateDiscoveryStatus.mutate(
          {
            data: {
              introductionId,
              updateType: 'matched-elsewhere',
              additionalDetails: additionalDetails ?? null,
            },
          },
          mutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
        break;
      case DiscoveryStatus.DifferentUpdate:
        if (!(await trigger('otherUpdateDetails'))) return;
        updateDiscoveryStatus.mutate(
          {
            data: {
              introductionId,
              updateType: 'other',
              additionalDetails: otherUpdateDetails ?? '',
            },
          },
          mutateOptions(DiscoveryCallModalStage.Update_Confirmed),
        );
        break;
    }
  };

  const getPrimaryCTA = (): ModalCallToAction => {
    switch (modalStage) {
      case DiscoveryCallModalStage.Update_Selection:
        // Inline statuses collect their fields under the dropdown and submit here; the rest
        // navigate to their own stage.
        if (isInlineStatus(selectedStatus)) {
          return { text: 'Save & continue', callback: submitInlineUpdate };
        }
        return {
          text: 'Continue',
          callback: async () => {
            if (!(await trigger('status'))) return;
            switch (selectedStatus) {
              case DiscoveryStatus.Matched:
                // Pros name/date the project; reps just confirm on a single screen
                setModalStage(
                  isPro ?
                    DiscoveryCallModalStage.Match_Confirming
                  : DiscoveryCallModalStage.Confirm_Match,
                );
                break;
              case DiscoveryStatus.NotAGoodFit:
                setModalStage(DiscoveryCallModalStage.Match_Declining);
                break;
              case DiscoveryStatus.Unresponsive:
                setModalStage(DiscoveryCallModalStage.Unresponsive);
                break;
              case DiscoveryStatus.MatchedElsewhere:
                // Only reached for pros — reps fill this inline above.
                setModalStage(DiscoveryCallModalStage.Matched_Elsewhere);
                break;
            }
          },
        };
      case DiscoveryCallModalStage.Matched_Elsewhere:
        return { text: 'Save & continue', callback: submitInlineUpdate };
      case DiscoveryCallModalStage.Unresponsive:
        return {
          text: 'Save & continue',
          callback: () => {
            updateDiscoveryStatus.mutate(
              {
                data: {
                  introductionId,
                  updateType: 'unresponsive',
                  additionalDetails: additionalDetails ?? '',
                },
              },
              mutateOptions(DiscoveryCallModalStage.Update_Confirmed),
            );
          },
        };
      case DiscoveryCallModalStage.Match_Declining:
        return {
          text: 'Save & continue',
          callback: async () => {
            // Reason + optional note are collected here; the decline is submitted on the next
            // screen's "Save & close" so the recommendations checkbox is part of the same request.
            if (!(await trigger('reason'))) return;
            setModalStage(DiscoveryCallModalStage.Match_Declined);
          },
        };
      case DiscoveryCallModalStage.Confirm_Match:
        return {
          text: 'Confirm match',
          callback: () => {
            updateDiscoveryStatus.mutate(
              {
                data: {
                  introductionId,
                  updateType: 'discovery-completed',
                  outcome: 'accepted-rep',
                  additionalDetails: additionalDetails ?? null,
                },
              },
              mutateOptions(DiscoveryCallModalStage.Match_Confirmed),
            );
          },
        };
      case DiscoveryCallModalStage.Match_Confirming:
        return {
          text: 'Save & continue',
          callback: () => {
            // A custom project (no name yet) needs naming before we can submit the acceptance
            if (!props.projectName) {
              setModalStage(DiscoveryCallModalStage.Custom_Naming);
            } else {
              setModalStage(DiscoveryCallModalStage.Pro_Confirm_Match);
            }
          },
        };
      case DiscoveryCallModalStage.Custom_Naming:
        return {
          text: 'Save & continue',
          callback: async () => {
            if (!(await trigger('projectName'))) return;
            setModalStage(DiscoveryCallModalStage.Pro_Confirm_Match);
          },
        };
      case DiscoveryCallModalStage.Pro_Confirm_Match:
        return {
          text: 'Confirm match',
          callback: () => {
            submitAcceptedPro();
          },
        };
      case DiscoveryCallModalStage.Match_Confirmed:
        return {
          text:
            userType === SimplifiedUserRole.PROFESSIONAL ?
              'Back to Project Hub'
            : 'View in My Projects',
          callback: () => {
            if (userType === SimplifiedUserRole.PROFESSIONAL) {
              closeModal();
            } else {
              navigate(`platform${ROUTES.DASHBOARD}${ROUTES.MY_PROJECTS}`);
            }
          },
        };
      case DiscoveryCallModalStage.Match_Declined:
        return {
          text: 'Save & close',
          callback: () => {
            updateDiscoveryStatus.mutate(
              {
                data: {
                  introductionId,
                  updateType: 'discovery-completed',
                  outcome: 'declined',
                  reason: selectedReason as AssignmentDeclinedReason,
                  additionalDetails: declineReasonDetails ?? null,
                  requestedMatchingSupport: requestedMatchingSupport ?? false,
                },
              },
              {
                onSuccess: () => {
                  setIsSubmissionErrored(false);
                  closeModal();
                },
                onError: () => setIsSubmissionErrored(true),
              },
            );
          },
        };
      default:
        return { text: 'Close', callback: closeModal };
    }
  };

  const getSecondaryCTA = (): ModalCallToAction | undefined => {
    switch (modalStage) {
      case DiscoveryCallModalStage.Update_Selection:
        return { text: 'Cancel', callback: closeModal };
      case DiscoveryCallModalStage.Matched_Elsewhere:
      case DiscoveryCallModalStage.Unresponsive:
      case DiscoveryCallModalStage.Match_Declining:
      case DiscoveryCallModalStage.Match_Confirming:
      case DiscoveryCallModalStage.Confirm_Match:
        return {
          text: 'Go back',
          callback: () => setModalStage(DiscoveryCallModalStage.Update_Selection),
        };
      case DiscoveryCallModalStage.Pro_Confirm_Match:
        return {
          text: 'Go back',
          callback: () =>
            setModalStage(
              props.projectName ?
                DiscoveryCallModalStage.Match_Confirming
              : DiscoveryCallModalStage.Custom_Naming,
            ),
        };
      case DiscoveryCallModalStage.Custom_Naming:
        return {
          text: 'Go back',
          callback: () => setModalStage(DiscoveryCallModalStage.Match_Confirming),
        };
      case DiscoveryCallModalStage.Match_Declined:
        return {
          text: 'Go back',
          callback: () => setModalStage(DiscoveryCallModalStage.Match_Declining),
        };
      case DiscoveryCallModalStage.Match_Confirmed:
        return { text: 'Close', callback: closeModal };
      default:
        return undefined;
    }
  };

  const secondaryCTA = getSecondaryCTA();

  return (
    <Modal
      isOpen={true}
      headerDivision='step'
      progress={getProgress(modalStage)}
      onClose={closeModal}
      variant='full-screen'
      title={'Provide a Match Update'}
      primaryCTA={{ ...getPrimaryCTA(), isDisabled: () => isSubmissionSaving }}
      secondaryCTA={
        secondaryCTA ? { ...secondaryCTA, isDisabled: () => isSubmissionSaving } : undefined
      }
    >
      {modalStage === DiscoveryCallModalStage.Update_Selection && (
        <div className='flex flex-col gap-4 items-center'>
          <UpdateSelectionContent
            setValue={setValue}
            selectedStatus={selectedStatus}
            userType={userType}
            formState={formState}
          />
          {selectedStatus === DiscoveryStatus.Scheduled && (
            <ScheduledContent
              callDate={callDate}
              setCallDate={setCallDate}
              minDate={tomorrow}
              control={control}
              isSaving={isSubmissionSaving}
            />
          )}
          {selectedStatus === DiscoveryStatus.StillDeciding && (
            <StillDecidingContent control={control} isSaving={isSubmissionSaving} />
          )}
          {selectedStatus === DiscoveryStatus.MatchedElsewhere && !isPro && (
            <MatchedElsewhereContent control={control} isSaving={isSubmissionSaving} />
          )}
          {selectedStatus === DiscoveryStatus.DifferentUpdate && (
            <OtherUpdateContent
              control={control}
              formState={formState}
              isSaving={isSubmissionSaving}
            />
          )}
        </div>
      )}
      {modalStage === DiscoveryCallModalStage.Matched_Elsewhere && (
        <MatchedElsewhereContent control={control} isSaving={isSubmissionSaving} showIntro />
      )}
      {modalStage === DiscoveryCallModalStage.Unresponsive && (
        <UnresponsiveContent
          control={control}
          otherPartyName={otherPartyName}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Declining && (
        <MatchDecliningContent
          control={control}
          register={register}
          setValue={setValue}
          formState={formState}
          selectedReason={selectedReason ?? undefined}
          isSaving={isSubmissionSaving}
          userAccountType={userType}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Declined && (
        <MatchDeclinedContent control={control} isSaving={isSubmissionSaving} />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Confirmed && (
        <MatchConfirmedContent otherPartyName={otherPartyName} userAccountType={userType} />
      )}
      {modalStage === DiscoveryCallModalStage.Update_Confirmed && (
        <UpdateConfirmedContent
          message={selectedStatus ? UPDATE_CONFIRMED_MESSAGES[selectedStatus] : undefined}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Confirm_Match && (
        <OrgRepConfirmMatchContent control={control} isSaving={isSubmissionSaving} />
      )}
      {modalStage === DiscoveryCallModalStage.Match_Confirming && (
        <MatchConfirmingContent
          startDate={startDate}
          setStartDate={setStartDate}
          endDate={endDate}
          setEndDate={setEndDate}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Custom_Naming && (
        <NameCustomProjectContent
          register={register}
          formState={formState}
          isSaving={isSubmissionSaving}
        />
      )}
      {modalStage === DiscoveryCallModalStage.Pro_Confirm_Match && (
        <ProConfirmMatch control={control} formState={formState} isSaving={isSubmissionSaving} />
      )}
      {isSubmissionErrored && (
        <div className='md:absolute md:left-0 md:right-0 text-center mt-12 text-error-500'>
          <p>Something went wrong while submitting your update. Please try again later.</p>
          <p>If the problem persists, please contact support.</p>
        </div>
      )}
    </Modal>
  );
};

const getProgress = (modalState: DiscoveryCallModalStage): number => {
  switch (modalState) {
    case DiscoveryCallModalStage.Update_Selection:
      return 10;
    case DiscoveryCallModalStage.Matched_Elsewhere:
    case DiscoveryCallModalStage.Unresponsive:
    case DiscoveryCallModalStage.Match_Confirming:
    case DiscoveryCallModalStage.Confirm_Match:
    case DiscoveryCallModalStage.Match_Declining:
      return 50;
    case DiscoveryCallModalStage.Custom_Naming:
      return 70;
    case DiscoveryCallModalStage.Match_Confirmed:
    case DiscoveryCallModalStage.Match_Declined:
    case DiscoveryCallModalStage.Update_Confirmed:
      return 100;
    default:
      return 20;
  }
};
